import React from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity, View } from 'react-native';

import { InfoIconCircle } from '@/assets/icons/info-circle';
import { ModalPullDownIndicator } from '@/components/PullDownIndicator';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { styles as modalStyles } from '@/feature/alerts/components/Modal';
import { designSystem } from '@/styles/styles';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { GameResult } from '../GameResultItem';
import { PlayerColumn, PlayerLostColumn, PlayerSearchingColumn } from '../PlayerMatchup/PlayerColumn';
import { PlayerMatchup } from '../PlayerMatchup/PlayerMatchup';
import { WinGradient } from './WinGradient';

type GameResultsMatchupProps = {
    gameResult: GameResult;
    onIconPress: () => void;
};

type GameResultsMatchupBaseProps = GameResultsMatchupProps & {
    userColumn: React.ReactNode;
    opponentColumn: React.ReactNode;
    description: string;
};

export const GameResultsModalBase = ({
    gameResult,
    userColumn,
    opponentColumn,
    description,
    onIconPress,
}: GameResultsMatchupBaseProps) => {
    return (
        <Box alignItems="center">
            <Box height={64} justifyContent="center" alignItems="center" width="100%">
                <Text variant="titleLarge2">{gameResult.name}</Text>
                <Text variant="bodySmall2" color={'neutral300'}>
                    {description}
                </Text>
                <Box position="absolute" right={16} top={10}>
                    <TouchableOpacity onPress={onIconPress}>
                        <InfoIconCircle />
                    </TouchableOpacity>
                </Box>
            </Box>
            <PlayerMatchup userColumn={userColumn} opponentColumn={opponentColumn} />
        </Box>
    );
};

const GameResultsModalPending = ({ gameResult, onIconPress }: GameResultsMatchupProps) => {
    const { t } = useTranslation('arcade_game_results');
    return (
        <GameResultsModalBase
            gameResult={gameResult}
            description={t('searching_for_opponent')}
            userColumn={<PlayerColumn playerType="you" score={''} avatarUrl={gameResult.avatar_url} />}
            opponentColumn={<PlayerSearchingColumn />}
            onIconPress={onIconPress}
        />
    );
};

// The Won state includes a gradient background. In order for the gradient background to be visible
// we had to:
// - Hide the default handle
// - Add a similar handle component to the top of the modal content
// There is a limition to this approach as the handle also animates when switching between the matchup screen and details screen
const GameResultsModalWon = ({ gameResult, onIconPress }: GameResultsMatchupProps) => {
    const { t } = useTranslation('arcade_game_results');
    return (
        <>
            <Box style={modalStyles.handle}>
                <View>
                    <ModalPullDownIndicator color={designSystem.colors.neutral400} />
                    <WinGradient />
                </View>
            </Box>
            <Box alignItems="center" mt="s10">
                <Text variant="titleLarge2">{gameResult.name}</Text>
                <Text
                    variant="bodySmall2"
                    fontFamily="Gamay"
                    fontSize={30}
                    fontWeight="800"
                    color={ds.colorToken.text.success}
                    lineHeight={36}
                    mt="s4"
                >
                    {t('you_won', { amount: toLocaleCurrency(gameResult.amount_won) })}
                </Text>
                <Box position="absolute" right={16}>
                    <TouchableOpacity onPress={onIconPress}>
                        <InfoIconCircle />
                    </TouchableOpacity>
                </Box>
            </Box>
            <PlayerMatchup
                userColumn={
                    <PlayerColumn playerType="you" score={gameResult.score} avatarUrl={gameResult.avatar_url} />
                }
                opponentColumn={
                    <PlayerLostColumn
                        playerType="opponent"
                        score={gameResult.opponents[0]?.score}
                        username={gameResult.opponents[0]?.username}
                        avatarUrl={gameResult.opponents[0]?.avatar_url}
                    />
                }
            />
        </>
    );
};

const GameResultsModalLost = ({ gameResult, onIconPress }: GameResultsMatchupProps) => {
    const { t } = useTranslation();
    return (
        <GameResultsModalBase
            gameResult={gameResult}
            description={`${t('lost')} ${toLocaleCurrency(gameResult.stake)}`}
            userColumn={
                <PlayerLostColumn playerType="you" score={gameResult.score} avatarUrl={gameResult.avatar_url} />
            }
            opponentColumn={
                <PlayerColumn
                    playerType="opponent"
                    score={gameResult.opponents[0]?.score}
                    username={gameResult.opponents[0]?.username}
                    avatarUrl={gameResult.opponents[0]?.avatar_url}
                />
            }
            onIconPress={onIconPress}
        />
    );
};

const GameResultsModalRefunded = ({ gameResult, onIconPress }: GameResultsMatchupProps) => {
    const { t } = useTranslation();
    return (
        <GameResultsModalBase
            gameResult={gameResult}
            description={`${t('refunded')} ${toLocaleCurrency(gameResult.stake)}`}
            userColumn={<PlayerColumn playerType="you" score={gameResult.score} avatarUrl={gameResult.avatar_url} />}
            opponentColumn={
                <PlayerColumn
                    playerType="opponent"
                    score={gameResult.opponents[0]?.score}
                    username={gameResult.opponents[0]?.username}
                    avatarUrl={gameResult.opponents[0]?.avatar_url}
                />
            }
            onIconPress={onIconPress}
        />
    );
};

export const GameResultMatchup = (props: GameResultsMatchupProps) => {
    const { gameResult } = props;
    switch (gameResult.status) {
        case 'PENDING':
            return <GameResultsModalPending {...props} />;
        case 'WON':
            return <GameResultsModalWon {...props} />;
        case 'LOST':
            return <GameResultsModalLost {...props} />;
        case 'REFUNDED':
        case 'DRAW':
            return <GameResultsModalRefunded {...props} />;
    }
};
